/**
 * `/notifications/preferences` - WHAT THE AUTHOR IS SENT: `getNotificationPreferences`,
 * `updateNotificationPreferences`. The address every monthly statement e-mail links to (./paths.ts).
 *
 * Register R-015 (lens findings U1-03, C4-02).
 *
 * TWO LISTS, BECAUSE THE PLATFORM HAS TWO KINDS OF CATEGORY. Of the fifteen an author has, two can
 * be switched (a tip, a supporter's message - phone only) and thirteen are ALWAYS ON: "a verdict on
 * the author's work or a fact about their money", which "the preference screen must render as fixed
 * rather than as a switch that fails". So an always-on category gets NO SWITCH AT ALL - not a
 * disabled one - and is never sent in a write, which keeps the platform's 422 `ALWAYS_ON_CATEGORY`
 * unreachable from this screen. Which kind a row is comes from the matrix's own `isAlwaysOn`, never
 * from a list typed here.
 *
 * ★ THE STATEMENT E-MAIL'S OWN ROW IS ALWAYS ON. `payout_statement` is transactional, so the link an
 * author follows from that e-mail lands on a page where that very e-mail cannot be switched off. The
 * server's header says so (cabinet-origin.provider.ts, "WHAT THIS LINK CANNOT DO"); this page says
 * it to the author, in the always-on list's own words, instead of leaving them hunting for a switch.
 *
 * A SWITCH SENDS ONE ITEM and draws the matrix the platform answers with, so what is shown is what
 * was stored. A change is not retroactive: the feed keeps what was already recorded.
 *
 * DESIGN 15 (the owner's live look at real cabinets, 2026-10-01): the way back as the prototype's
 * outline pill in the header, and each list a section card (the kit's SectionCard) - the
 * same two lists, the same switches, the same captions naming the two tables.
 *
 * ★ THE LANGUAGE MOONX WRITES IN (design 29, W6). The last card chooses the language of the
 * author's e-mails and push notifications: the person's `languageCode` (decision-log row 115 item
 * 5), which push and e-mail read. It is NOT the cabinet's display language, which lives in the
 * browser, and this card is the one place the cabinet writes it - on purpose, with Save, as one
 * partial `PATCH /v1/profile` of `{ languageCode }`. Never chosen (`null`, sent in English today),
 * the display language is offered, and the card says plainly that nothing is saved yet.
 */
import { Button, Group, NativeSelect, Stack, Switch, Table, Text } from '@mantine/core';
import { ArrowLeft, Globe, Lock, SlidersHorizontal } from '@moonx/ui';
import { useCallback, useMemo, useState } from 'react';
import { createApplicationApi } from '../../features/application/api/create';
import type { ApplicationApi } from '../../features/application/api/types';
import { useCabinetApi } from '../../features/cabinet/api';
import type { OperationResult } from '../../features/cabinet/api';
import {
  CabinetPageHeader,
  ErrorState,
  LoadingState,
  SectionCard,
} from '../../features/cabinet/components';
import { useAction, useAsync } from '../../features/cabinet/state';
import { InAppAnchor } from '../../features/shell/InAppAnchor';
import {
  CABINET_LANGUAGES,
  DEFAULT_CABINET_LANGUAGE,
  cabinetLanguageOf,
  i18n,
  useTranslation,
} from '../../i18n';
import { categoryWords, channelSentWords, channelWords } from './notification-words';
import { NOTIFICATIONS_PATH } from './paths';
import classes from './notifications.module.css';

type Preferences = OperationResult<'getNotificationPreferences'>;
type PreferenceItem = Preferences['items'][number];
type Channel = 'push' | 'email';

interface Change {
  readonly categoryCode: string;
  readonly channel: Channel;
  readonly enabled: boolean;
}

const CHANNELS: readonly Channel[] = ['push', 'email'];

function channelsIn(item: PreferenceItem): string {
  const names = item.channels.map((entry) => channelSentWords(entry.channel));
  return names.length === 0
    ? i18n.t('engagement:notifications.channelSent.cabinetOnly')
    : new Intl.ListFormat(i18n.language, { type: 'conjunction' }).format(names);
}

/** The two profile operations the language card uses - the application seam's own client's. */
export type ProfileLanguageApi = Pick<ApplicationApi, 'getOwnProfile' | 'updateOwnProfile'>;

export interface NotificationPreferencesRouteProps {
  /**
   * Where the language card reads and writes `languageCode`. Left out, the application seam's
   * client (`createApplicationApi`): this screen is not under `ApplicationApiProvider`.
   */
  readonly profileApi?: ProfileLanguageApi;
}

export function NotificationPreferencesRoute({ profileApi }: NotificationPreferencesRouteProps) {
  const { t } = useTranslation('engagement');
  const api = useCabinetApi();
  const read = useCallback((signal: AbortSignal) => api.getNotificationPreferences(signal), [api]);
  const loaded = useAsync(read, 'notifications:preferences');
  /** The matrix the last write answered with; until one lands, the read's. */
  const [written, setWritten] = useState<Preferences | undefined>(undefined);
  const [pending, setPending] = useState<string | null>(null);
  const update = useAction((change: Change) =>
    api.updateNotificationPreferences({ items: [change] }),
  );

  const matrix = written ?? loaded.data;
  const switchable = matrix?.items.filter((item) => !item.isAlwaysOn) ?? [];
  const alwaysOn = matrix?.items.filter((item) => item.isAlwaysOn) ?? [];

  const onToggle = (change: Change) => {
    setPending(`${change.categoryCode}:${change.channel}`);
    void update.run(change).then((answer) => {
      setPending(null);
      if (answer !== undefined) {
        setWritten(answer);
      }
    });
  };

  return (
    <Stack gap="lg">
      <CabinetPageHeader
        title={t('notifications.preferences.title')}
        lede={t('notifications.preferences.lede')}
        actions={
          <InAppAnchor
            href={NOTIFICATIONS_PATH}
            className={classes.pillLink}
            c="oklch(var(--mx-text))"
            underline="never"
          >
            <ArrowLeft size={14} className={classes.pillIcon} aria-hidden />
            {t('notifications.preferences.back')}
          </InAppAnchor>
        }
      />

      {loaded.status === 'loading' && matrix === undefined ? (
        <LoadingState label={t('notifications.preferences.reading')} rows={4} />
      ) : loaded.status === 'error' && matrix === undefined ? (
        <ErrorState
          title={t('notifications.preferences.failed')}
          error={loaded.error}
          onRetry={loaded.refetch}
        />
      ) : matrix === undefined ? null : (
        <>
          {update.error === undefined ? null : (
            <ErrorState title={t('notifications.preferences.notChanged')} error={update.error} />
          )}
          <SwitchableList items={switchable} pending={pending} onToggle={onToggle} />
          <AlwaysOnList items={alwaysOn} />
        </>
      )}

      {/* Its own read and write: the language does not wait on the switches, nor they on it. */}
      <NotificationLanguage given={profileApi} />
    </Stack>
  );
}

function SwitchableList({
  items,
  pending,
  onToggle,
}: {
  readonly items: readonly PreferenceItem[];
  readonly pending: string | null;
  readonly onToggle: (change: Change) => void;
}) {
  const { t } = useTranslation('engagement');
  return (
    <SectionCard title={t('notifications.preferences.switchable.title')} icon={SlidersHorizontal}>
      {items.length === 0 ? (
        <Text size="sm" c="dimmed">
          {t('notifications.preferences.switchable.none')}
        </Text>
      ) : (
        <Table verticalSpacing="sm">
          <Table.Caption>{t('notifications.preferences.switchable.caption')}</Table.Caption>
          <Table.Thead>
            <Table.Tr>
              <Table.Th scope="col">{t('notifications.preferences.notification')}</Table.Th>
              {CHANNELS.map((channel) => (
                <Table.Th key={channel} scope="col">
                  {channelWords(channel)}
                </Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {items.map((item) => {
              const words = categoryWords(item.categoryCode, item.label);
              return (
                <Table.Tr key={item.categoryCode}>
                  <Table.Th scope="row" style={{ fontWeight: 400 }}>
                    {words}
                  </Table.Th>
                  {CHANNELS.map((channel) => {
                    const entry = item.channels.find((candidate) => candidate.channel === channel);
                    return (
                      <Table.Td key={channel}>
                        {entry === undefined ? (
                          // A channel the category does not support is simply absent from the
                          // matrix; naming it would be a 422 (NotificationPreferencesWrite).
                          <Text size="sm" c="dimmed">
                            {t('notifications.preferences.switchable.notOffered')}
                          </Text>
                        ) : (
                          <Switch
                            aria-label={t('notifications.preferences.switchable.switchLabel', {
                              category: words,
                              channel: channelWords(channel),
                            })}
                            checked={entry.enabled}
                            disabled={pending !== null}
                            onChange={(event) => {
                              onToggle({
                                categoryCode: item.categoryCode,
                                channel,
                                enabled: event.currentTarget.checked,
                              });
                            }}
                          />
                        )}
                      </Table.Td>
                    );
                  })}
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      )}
    </SectionCard>
  );
}

function AlwaysOnList({ items }: { readonly items: readonly PreferenceItem[] }) {
  const { t } = useTranslation('engagement');
  if (items.length === 0) {
    return null;
  }
  return (
    <SectionCard title={t('notifications.preferences.alwaysOn.title')} icon={Lock}>
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          {t('notifications.preferences.alwaysOn.note')}
        </Text>
        <Table verticalSpacing="xs">
          <Table.Caption>{t('notifications.preferences.alwaysOn.caption')}</Table.Caption>
          <Table.Thead>
            <Table.Tr>
              <Table.Th scope="col">{t('notifications.preferences.notification')}</Table.Th>
              <Table.Th scope="col">{t('notifications.preferences.alwaysOn.sent')}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {items.map((item) => (
              <Table.Tr key={item.categoryCode}>
                <Table.Th scope="row" style={{ fontWeight: 400 }}>
                  {categoryWords(item.categoryCode, item.label)}
                </Table.Th>
                <Table.Td>
                  <Text size="sm">{channelsIn(item)}</Text>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Stack>
    </SectionCard>
  );
}

/** A language by its own name ("Deutsch"), or its code when it is not one of the fourteen. */
function languageNamed(code: string): string {
  return CABINET_LANGUAGES.find((language) => language.code === code)?.name ?? code;
}

/** The language card (see the header): read, choose, Save. */
function NotificationLanguage({ given }: { readonly given: ProfileLanguageApi | undefined }) {
  const { t } = useTranslation('engagement');
  // Made once per mount when none is handed in, as the profile preview makes its own.
  const api = useMemo(() => given ?? createApplicationApi(), [given]);
  const read = useCallback((signal: AbortSignal) => api.getOwnProfile({ signal }), [api]);
  const profile = useAsync(read, 'notifications:language');
  const save = useAction((languageCode: string) => api.updateOwnProfile({ languageCode }));
  /** What the last save stored; until one lands, the read's. */
  const [stored, setStored] = useState<string | null | undefined>(undefined);
  const [chosen, setChosen] = useState<string | null>(null);
  const [justSaved, setJustSaved] = useState(false);

  const current = stored === undefined ? (profile.data?.languageCode ?? null) : stored;
  // Never chosen: the language the cabinet is shown in is offered - and not saved until Save.
  const offered = cabinetLanguageOf(i18n.language) ?? DEFAULT_CABINET_LANGUAGE;
  const value = chosen ?? current ?? offered;
  const options = [
    ...(current === null || CABINET_LANGUAGES.some((language) => language.code === current)
      ? []
      : [{ value: current, label: current }]),
    ...CABINET_LANGUAGES.map((language) => ({ value: language.code, label: language.name })),
  ];

  const onSave = () => {
    setJustSaved(false);
    void save.run(value).then((answer) => {
      if (answer !== undefined) {
        setStored(answer.languageCode ?? value);
        setChosen(null);
        setJustSaved(true);
      }
    });
  };

  return (
    <SectionCard title={t('notifications.language.title')} icon={Globe}>
      {profile.status === 'loading' && profile.data === undefined ? (
        <LoadingState label={t('notifications.language.reading')} />
      ) : profile.status === 'error' && profile.data === undefined ? (
        <ErrorState
          title={t('notifications.language.readFailed')}
          error={profile.error}
          onRetry={profile.refetch}
        />
      ) : (
        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            {t('notifications.language.note')}
          </Text>
          <Group gap="sm" align="flex-end">
            <NativeSelect
              label={t('notifications.language.label')}
              data={options}
              value={value}
              disabled={save.isRunning}
              onChange={(event) => {
                setChosen(event.currentTarget.value);
                setJustSaved(false);
              }}
            />
            <Button onClick={onSave} loading={save.isRunning}>
              {t('notifications.language.save')}
            </Button>
          </Group>
          {current === null ? <Text size="sm">{t('notifications.language.notSaved')}</Text> : null}
          {justSaved && current !== null ? (
            <Text size="sm" role="status">
              {t('notifications.language.saved', { language: languageNamed(current) })}
            </Text>
          ) : null}
          {save.error === undefined ? null : (
            <ErrorState title={t('notifications.language.failed')} error={save.error} />
          )}
        </Stack>
      )}
    </SectionCard>
  );
}
